import React, { useState } from 'react';
import { Platform, TextInput, type TextInputProps } from 'react-native';
import { IconArrowRight, IconThumbUp } from '@tabler/icons-react-native';

import { fieldInputReset } from '../field.styles';

import { Box } from 'app/shopify/theme';

import { FieldAction, FieldBorder, useField, type FieldIconSlot, type FieldSize, type FieldState } from '../field';

export interface MessageFieldProps extends Omit<TextInputProps, 'style'> {
  size?: FieldSize;
  state?: FieldState;
  disabled?: boolean;
  onSend?: (value: string) => void;
  onReaction?: () => void;
  showReaction?: boolean;
  reactionIcon?: FieldIconSlot;
  sendAccessibilityLabel?: string;
  reactionAccessibilityLabel?: string;
  containerStyle?: React.ComponentProps<typeof Box>['style'];
  inputStyle?: TextInputProps['style'];
  ref?: React.Ref<TextInput>;
}

export function MessageField({
  value,
  defaultValue,
  onChangeText,
  placeholder = 'Send a message',
  size = 'default',
  state = 'enabled',
  disabled = false,
  readOnly = false,
  editable = true,
  onSend,
  onReaction,
  showReaction = true,
  reactionIcon,
  sendAccessibilityLabel = 'Send message',
  reactionAccessibilityLabel = 'Send a reaction',
  accessibilityLabel,
  accessibilityState,
  onFocus,
  onBlur,
  onSubmitEditing,
  containerStyle,
  inputStyle,
  ref,
  ...inputProps
}: MessageFieldProps) {
  const { currentValue, setUncontrolledValue, setEditing, field, theme, composing, canSend } = useMessageComposer({
    value,
    defaultValue,
    size,
    state,
    disabled,
    readOnly,
    editable,
    onSend,
  });

  function send() {
    if (canSend) onSend?.(currentValue);
  }

  return (
    <Box flexDirection="row" alignItems="center" gap="spacing.general.x2" style={containerStyle}>
      <Box
        flex={1}
        minWidth={0}
        minHeight={field.height}
        flexDirection="row"
        alignItems="center"
        gap="spacing.general.x1"
        padding="spacing.general.x1"
        paddingRight={!field.small && !currentValue && !composing ? 'spacing.general.x2' : 'spacing.general.x1'}
        borderRadius="radius.component.fields.input"
        style={{ backgroundColor: composing ? 'transparent' : theme.colors['surface.primary-neutral'] }}
        {...(Platform.OS === 'web'
          ? { onMouseEnter: () => field.setHovered(true), onMouseLeave: () => field.setHovered(false) }
          : {})}
      >
        <Box
          flex={1}
          minWidth={0}
          paddingLeft={field.small ? 'spacing.general.x2' : 'spacing.general.x3'}
          paddingRight="spacing.general.x1"
        >
          <TextInput
            {...inputProps}
            ref={ref}
            value={currentValue}
            placeholder={placeholder}
            placeholderTextColor={theme.colors['text.tertiary']}
            accessibilityLabel={accessibilityLabel ?? placeholder}
            accessibilityState={{ ...accessibilityState, disabled: field.isDisabled }}
            editable={!field.isDisabled && !field.isReadOnly}
            readOnly={field.isReadOnly}
            returnKeyType="send"
            onChangeText={(text) => {
              if (!field.interactive) return;
              if (value === undefined) setUncontrolledValue(text);
              onChangeText?.(text);
            }}
            onFocus={(event) => {
              field.setFocused(true);
              setEditing(true);
              onFocus?.(event);
            }}
            onBlur={(event) => {
              field.setFocused(false);
              if (!currentValue) setEditing(false);
              onBlur?.(event);
            }}
            onSubmitEditing={(event) => {
              send();
              onSubmitEditing?.(event);
            }}
            style={[
              fieldInputReset.input,
              theme.textVariants[currentValue ? 'bodyBaseMedium' : 'bodyBase'],
              { color: field.textColor, minHeight: field.iconSize, marginLeft: theme.spacing['spacing.general.x1'] },
              inputStyle,
            ]}
          />
        </Box>
        {!composing && showReaction ? (
          <FieldAction
            icon={reactionIcon ?? <IconThumbUp strokeWidth={2} />}
            size={field.buttonSize}
            iconSize={field.iconSize}
            accessibilityLabel={reactionAccessibilityLabel}
            onPress={onReaction}
            disabled={!field.interactive || !onReaction}
          />
        ) : null}
        <FieldBorder field={field} />
      </Box>
      {composing ? (
        <FieldAction
          icon={<IconArrowRight strokeWidth={2} />}
          size={field.height}
          iconSize={field.iconSize}
          accessibilityLabel={sendAccessibilityLabel}
          onPress={send}
          disabled={!canSend}
          primary
        />
      ) : null}
    </Box>
  );
}

function useMessageComposer({
  value,
  defaultValue,
  size,
  state,
  disabled,
  readOnly,
  editable,
  onSend,
}: Pick<
  MessageFieldProps,
  'value' | 'defaultValue' | 'size' | 'state' | 'disabled' | 'readOnly' | 'editable' | 'onSend'
>) {
  const [uncontrolledValue, setUncontrolledValue] = useState(defaultValue ?? '');
  const [editing, setEditing] = useState(false);
  const currentValue = value ?? uncontrolledValue;
  const field = useField({ size, state, disabled, readOnly: readOnly || !editable });
  const { theme } = field;
  // Keep the send action mounted after input blur so mouse/touch activation can finish.
  const composing = field.interactive && (field.active || editing);
  const canSend = field.interactive && currentValue.trim().length > 0 && !!onSend;

  return { currentValue, setUncontrolledValue, setEditing, field, theme, composing, canSend };
}
